// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Curated display registry for the Fund Details page (Fund Details Enrichment
 * Tier 1 — PRD §9). Maps the fund's verbatim `extra_fields` keys to a human
 * label + a section + an optional scalar formatter, so the page renders the
 * already-stored provider data provider-agnostically. Coverage is uneven across
 * providers by design — every field hides when absent, and any present-but-
 * unmapped scalar still surfaces under "Additional details" so we never silently
 * drop data.
 */

export type FundDetailSection = 'overview' | 'keyFacts' | 'documents';

export interface FieldSpec {
  /** `extra_fields` key. */
  key: string;
  label: string;
  section: FundDetailSection;
  /** Scalar formatter; defaults to String(value). */
  format?: (value: unknown) => string;
}

const asText = (v: unknown): string => (v == null ? '' : String(v).trim());

/**
 * Registry order is display order within each section. The same concept can
 * appear under several provider keys — list each; the first present one wins
 * per section (see `pickFieldSpecs`).
 */
export const FUND_FIELD_REGISTRY: FieldSpec[] = [
  // ── Overview (narrative) ──────────────────────────────────────────────
  { key: 'fund_objective', label: 'Objective', section: 'overview' },
  { key: 'investment_objective', label: 'Objective', section: 'overview' },
  { key: 'fund_description', label: 'Objective', section: 'overview' },
  { key: 'investment_strategy', label: 'Strategy', section: 'overview' },
  { key: 'investment_style', label: 'Investment style', section: 'overview' },
  { key: 'managed_type', label: 'Management style', section: 'overview' },

  // ── Key facts (scalars beyond the typed FundDTO promotions) ───────────
  { key: 'benchmark', label: 'Benchmark', section: 'keyFacts' },
  { key: 'benchmark_index', label: 'Benchmark', section: 'keyFacts' },
  { key: 'isin', label: 'ISIN', section: 'keyFacts' },
  { key: 'sedol', label: 'SEDOL', section: 'keyFacts' },
  { key: 'currency', label: 'Currency', section: 'keyFacts' },
  { key: 'base_currency', label: 'Currency', section: 'keyFacts' },
  { key: 'domicile', label: 'Domicile', section: 'keyFacts' },
  { key: 'inc_acc_type', label: 'Income / accumulation', section: 'keyFacts' },
  { key: 'tax', label: 'Tax basis', section: 'keyFacts' },
  { key: 'star_rating', label: 'Star rating', section: 'keyFacts', format: (v) => `${asText(v)}★` },
  {
    key: 'protected_fund',
    label: 'Protected fund',
    section: 'keyFacts',
    format: (v) => (v === true || asText(v).toUpperCase() === 'Y' ? 'Yes' : 'No'),
  },
  { key: 'number_of_holdings', label: 'Holdings', section: 'keyFacts' },
  { key: 'yield_to_maturity', label: 'Yield to maturity', section: 'keyFacts' },
  { key: 'duration', label: 'Duration', section: 'keyFacts' },
  { key: 'avg_credit_rating', label: 'Avg credit rating', section: 'keyFacts' },

  // ── Documents ─────────────────────────────────────────────────────────
  { key: 'monthly_factsheet_url', label: 'Monthly factsheet', section: 'documents' },
  { key: 'responsive_factsheet_url', label: 'Factsheet (web)', section: 'documents' },
  { key: 'sid_url', label: 'Significant Information Document', section: 'documents' },
  { key: 'document_pdf_url', label: 'Fund document', section: 'documents' },
  { key: 'fund_updates_url', label: 'Fund updates', section: 'documents' },
];

/** Keys the page consumes elsewhere (typed promotions / structured / handled) — excluded from the generic "Additional details" fallback. */
export const CONSUMED_EXTRA_KEYS = new Set<string>([
  ...FUND_FIELD_REGISTRY.map((f) => f.key),
  // typed FundDTO promotions coalesced from these keys
  'sfdr', 'sfdr_article', 'sfdr_classification', 'sfdr_rating',
  'asset_class', 'scorecard_category', 'sector',
  'fund_manager', 'fund_managers',
  'fund_size', 'fund_size_millions',
  'amc', 'fund_amc', 'additional_amc', 'ocf',
  // structured composition rendered by dedicated normalizers
  'top_10_holdings', 'holdings', 'asset_breakdown', 'sector_split',
  'strategic_asset_allocation', 'tactical_asset_allocation',
  // nested containers / bulky internals not for a flat list
  'classification', 'factsheet', 'prices', 'performance', 'performance_rankings',
  'performance_analysis', 'calendar_years', 'risk', 'discrete_performance',
]);

export interface ResolvedField {
  label: string;
  value: string;
  /** True when the value is a URL (render as a link). */
  isUrl: boolean;
}

/** Resolve the display fields for one section: first present key per label, formatted, non-blank. */
export function pickFieldSpecs(
  extra: Record<string, unknown> | null | undefined,
  section: FundDetailSection
): ResolvedField[] {
  if (!extra) return [];
  const out: ResolvedField[] = [];
  const seenLabels = new Set<string>();
  for (const spec of FUND_FIELD_REGISTRY) {
    if (spec.section !== section) continue;
    if (seenLabels.has(spec.label)) continue;
    const raw = extra[spec.key];
    if (raw == null || String(raw).trim() === '') continue;
    const value = spec.format ? spec.format(raw) : String(raw).trim();
    if (!value) continue;
    out.push({ label: spec.label, value, isUrl: /^https?:\/\//i.test(String(raw).trim()) });
    seenLabels.add(spec.label);
  }
  return out;
}

/** Present scalar keys not consumed anywhere else — the never-drop-data fallback. */
export function additionalScalarFields(
  extra: Record<string, unknown> | null | undefined
): ResolvedField[] {
  if (!extra) return [];
  const out: ResolvedField[] = [];
  for (const [key, raw] of Object.entries(extra)) {
    if (CONSUMED_EXTRA_KEYS.has(key)) continue;
    if (raw == null) continue;
    if (typeof raw === 'object') continue; // objects/arrays aren't flat scalars
    const value = String(raw).trim();
    if (!value) continue;
    out.push({ label: humanizeKey(key), value, isUrl: /^https?:\/\//i.test(value) });
  }
  return out;
}

export interface BreakdownRow {
  label: string;
  percent: number;
}

/**
 * Best-effort normalization of a provider breakdown/holdings array into
 * `{label, percent}` rows. Returns null when the shape isn't confidently an
 * array of name+percent objects (so the caller renders nothing rather than
 * garbage).
 */
export function normalizeBreakdown(value: unknown): BreakdownRow[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const rows: BreakdownRow[] = [];
  for (const item of value) {
    if (item == null || typeof item !== 'object') return null;
    const rec = item as Record<string, unknown>;
    const label = firstString(rec, ['name', 'label', 'Name', 'HoldingName', 'company', 'category', 'sector']);
    const percent = firstNumber(rec, ['percent', 'pct', 'Percent', 'weight', 'value', 'allocation']);
    if (label == null || percent == null) return null;
    rows.push({ label, percent });
  }
  return rows;
}

function firstString(rec: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return null;
}

function firstNumber(rec: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(n) && v.trim() !== '') return n;
    }
  }
  return null;
}

function humanizeKey(key: string): string {
  const s = key.replace(/_/g, ' ').replace(/\burl\b/i, 'URL').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
